/**
 * @fileoverview Mock backend for the Audit Log.
 * @description Registers GET /audit/events (Search Audit Events) and GET /audit/events/{id} (Get
 * Audit Event) on this feature's mock adapter, answering in the backend's envelopes with its error
 * codes: a missing or malformed `from`/`to`, an inverted or over-long range, and an unknown id.
 * Like the real API, no tenant parameter means platform-level events only, `tenantId` selects one
 * tenant and `tenantScope=ALL` selects every tenant, and every response reports the scope searched.
 * It doesn't model the caller's own authority, rate limits or MFA step-up. A no-op when
 * VITE_ENABLE_MOCK_API is false.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { auditMockAdapter } from './audit-mock-adapter'
import { fail, ok, pageWithScope, queryParams } from './audit-mock-response.utils'
import { findAuditEvent, listAuditEvents, toSummary } from './audit-mock-store'

import type { AuditEventDetail, AuditSearchScope } from '@/features/audit/types/audit.types'
import type { AxiosRequestConfig } from 'axios'

const DETAIL_PATH = /^\/audit\/events\/[^/]+$/
const MAX_RANGE_MS = 365 * 24 * 60 * 60 * 1000
const UTC_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2}(\.\d{1,9})?)?Z$/
const VOCABULARY = /^[A-Za-z0-9_.:-]+$/

/**
 * @description Checks the required date range the way the backend does.
 * @param {Record<string, string | undefined>} params - The query params.
 * @returns {[number, object] | null} A 400 reply naming the problem, or null when the range is fine.
 */
function validateRange(params: Record<string, string | undefined>): [number, object] | null {
  for (const name of ['from', 'to'] as const) {
    const value = params[name]
    if (!value || !UTC_INSTANT.test(value) || Number.isNaN(Date.parse(value))) {
      return fail(400, '0001', `${name} must be an ISO 8601 UTC date-time (ending in Z)`)
    }
  }

  const from = Date.parse(params.from as string)
  const to = Date.parse(params.to as string)
  if (from > to) return fail(400, '0001', 'from must not be later than to')
  if (to - from > MAX_RANGE_MS) return fail(400, '7005', 'The requested date range is too large')
  return null
}

/**
 * @description Whether an event matches the search's range and filters.
 * @param {AuditEventDetail} event - The event.
 * @param {Record<string, string | undefined>} params - The query params.
 * @returns {boolean} True when every given filter matches.
 */
function matches(event: AuditEventDetail, params: Record<string, string | undefined>): boolean {
  const at = Date.parse(event.occurredAt)
  const inScope =
    params.tenantScope === 'ALL' ||
    (params.tenantId ? event.tenant?.tenantId === params.tenantId : !event.tenant)

  return (
    inScope &&
    at >= Date.parse(params.from as string) &&
    at <= Date.parse(params.to as string) &&
    (!params.result || event.result === params.result) &&
    (!params.actorType || event.actor.actorType === params.actorType) &&
    (!params.eventType || event.eventType === params.eventType) &&
    (!params.eventCategory || event.eventCategory === params.eventCategory) &&
    (!params.sourceModule || event.sourceModule === params.sourceModule)
  )
}

/**
 * @description Names the scope a search covered, for `meta.searchScope`.
 * @param {Record<string, string | undefined>} params - The query params.
 * @returns {AuditSearchScope} The scope.
 */
function scopeOf(params: Record<string, string | undefined>): AuditSearchScope {
  if (params.tenantScope === 'ALL') return { type: 'ALL_TENANTS' }
  return params.tenantId ? { type: 'TENANT', tenantId: params.tenantId } : { type: 'PLATFORM' }
}

/**
 * @description Pulls the event id out of a /audit/events/{id} URL.
 * @param {AxiosRequestConfig} config - The request config.
 * @returns {string} The event id.
 */
function eventIdFrom(config: AxiosRequestConfig): string {
  return decodeURIComponent(config.url?.split('/')[3] ?? '')
}

if (auditMockAdapter) {
  const adapter = auditMockAdapter

  adapter.onGet('/audit/events').reply((config) => {
    const params = queryParams(config)
    const rangeProblem = validateRange(params)
    if (rangeProblem) return rangeProblem

    for (const name of ['eventType', 'sourceModule'] as const) {
      const value = params[name]
      if (value !== undefined && !VOCABULARY.test(value)) {
        return fail(400, '0001', `${name} may contain only letters, digits and _ . : -`)
      }
    }

    const sorted = listAuditEvents().filter((event) => matches(event, params))
    const ordered = params.sortDirection === 'ASC' ? [...sorted].reverse() : sorted
    return pageWithScope(params, ordered.map(toSummary), scopeOf(params))
  })

  adapter.onGet(DETAIL_PATH).reply((config) => {
    const event = findAuditEvent(eventIdFrom(config))
    return event ? [200, ok(event)] : fail(404, '7001', 'Audit event not found')
  })
}
